import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Target, 
  Lightbulb, 
  FolderOpen, 
  Plus,
  Trash2,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Edit3
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { DbChallenge, DbChallengeSolution, SOLUTION_TYPES } from '@/hooks/useChallengeLab';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface ChallengeHistoryPanelProps {
  challenges: DbChallenge[];
  solutions: DbChallengeSolution[];
  selectedChallengeId: string | null;
  onSelectChallenge: (id: string | null) => void;
  onNewChallenge: () => void;
  onDeleteChallenge: (id: string) => void;
  onViewSolution: (solution: DbChallengeSolution) => void;
  newChallengeDisabled?: boolean;
}

const statusConfig: Record<string, { label: string; icon: typeof Edit3; className: string }> = {
  draft: {
    label: 'En progreso',
    icon: Edit3,
    className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  },
  active: {
    label: 'Identificado',
    icon: CheckCircle2,
    className: 'bg-primary/10 text-primary border-primary/20',
  },
  completed: {
    label: 'Con soluciones',
    icon: FolderOpen,
    className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  },
};

export function ChallengeHistoryPanel({
  challenges,
  solutions,
  selectedChallengeId,
  onSelectChallenge,
  onNewChallenge,
  onDeleteChallenge,
  onViewSolution,
  newChallengeDisabled,
}: ChallengeHistoryPanelProps) {
  const { user } = useAuth();
  
  // Load all challenge chat sessions to show titles
  const challengeIds = challenges.map(c => c.id);
  const { data: sessionTitles = {} } = useQuery({
    queryKey: ['challenge_session_titles', challengeIds.join(',')],
    queryFn: async () => {
      if (!user || challengeIds.length === 0) return {};
      const { data, error } = await supabase
        .from('chat_sessions')
        .select('context_id, title')
        .eq('context_type', 'challenge')
        .eq('user_id', user.id)
        .in('context_id', challengeIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data || []).forEach(s => {
        if (s.context_id && s.title) map[s.context_id] = s.title;
      });
      return map;
    },
    enabled: !!user && challengeIds.length > 0,
  });

  const getSolutionsForChallenge = (challengeId: string) => {
    return solutions.filter(s => s.challenge_id === challengeId);
  };

  const getStatusInfo = (status: string) => {
    return statusConfig[status] || statusConfig.draft;
  };

  return (
    <Card className="glass-card h-full flex flex-col">
      <CardHeader className="pb-3 border-b border-border/50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            Mis Retos
          </CardTitle>
          <Button size="sm" onClick={onNewChallenge} className="gap-1.5 h-8" disabled={newChallengeDisabled}
            title={newChallengeDisabled ? 'Límite alcanzado para usuarios gratuitos' : undefined}>
            <Plus className="w-4 h-4" />
            Nuevo Chat
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 p-0 overflow-hidden">
        {/* Radix envuelve el contenido en un div display:table que se ensancha con
            títulos largos y desborda el panel: se fuerza a block para que corte/ajuste. */}
        <ScrollArea className="h-full [&>[data-radix-scroll-area-viewport]>div]:!block">
          <div className="p-3 space-y-2">
            {challenges.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium">No hay retos identificados</p>
                <p className="text-xs mt-1">Usa el asistente para identificar tu primer reto</p>
              </div>
            ) : (
              challenges.map((challenge) => {
                const challengeSolutions = getSolutionsForChallenge(challenge.id);
                const isSelected = selectedChallengeId === challenge.id;
                const statusInfo = getStatusInfo(challenge.status || 'draft');
                const StatusIcon = statusInfo.icon;
                const displayTitle = challenge.title || 'Reto en identificación...';
                const sessionTitle = sessionTitles[challenge.id];
                
                return (
                  <div
                    key={challenge.id}
                    className={`group rounded-xl transition-all duration-200 ${
                      isSelected
                        ? 'bg-primary/5 border-2 border-primary/30 shadow-sm'
                        : 'bg-card hover:bg-muted/50 border border-border/50 hover:border-border'
                    }`}
                  >
                    <div
                      className="p-3 cursor-pointer"
                      onClick={() => onSelectChallenge(isSelected ? null : challenge.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                        }`}>
                          <StatusIcon className="w-4 h-4" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p title={displayTitle} className={`font-medium text-sm leading-tight line-clamp-2 break-words ${
                            challenge.title ? 'text-foreground' : 'text-muted-foreground italic'
                          }`}>
                            {displayTitle}
                          </p>
                          {sessionTitle && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5" title={sessionTitle}>
                              {sessionTitle}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {format(new Date(challenge.created_at), "d MMM yyyy", { locale: es })}
                            </span>
                            
                            {challengeSolutions.length > 0 && (
                              <Badge variant="secondary" className="text-[10px] h-5 px-1.5 gap-0.5">
                                <FolderOpen className="w-3 h-3" />
                                {challengeSolutions.length}
                              </Badge>
                            )}
                            
                            <Badge variant="outline" className={`text-[10px] h-5 px-1.5 ${statusInfo.className}`}>
                              {statusInfo.label}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center shrink-0">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="flex items-center gap-2">
                                  <AlertTriangle className="w-5 h-5 text-destructive" />
                                  Eliminar Reto
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  ¿Estás seguro de eliminar "{displayTitle}"? Esta acción también eliminará todas las soluciones asociadas.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => onDeleteChallenge(challenge.id)}
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
